import React from 'react';

function CookiePolicy() {
  const cookieTypes = [
    {
      title: 'Essential Cookies',
      icon: 'fa-lock',
      description: 'These cookies are necessary for the website to function properly and cannot be disabled.',
      examples: [
        'Authentication cookies to verify your identity',
        'Security cookies to protect against fraud',
        'Session cookies to remember your preferences during your visit'
      ],
      duration: 'Session or up to 1 year'
    },
    {
      title: 'Analytics Cookies',
      icon: 'fa-chart-line',
      description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information.',
      examples: [
        'Google Analytics to track page views and user behavior',
        'Performance monitoring to identify technical issues',
        'Heat mapping to understand user engagement'
      ],
      duration: 'Up to 2 years'
    },
    {
      title: 'Functional Cookies',
      icon: 'fa-cog',
      description: 'These cookies enable enhanced functionality and personalization, such as remembering your preferences.',
      examples: [
        'Language preference cookies',
        'Region or timezone settings',
        'Accessibility preferences',
        'Customized content display settings'
      ],
      duration: 'Up to 1 year'
    },
    {
      title: 'Marketing Cookies',
      icon: 'fa-bullhorn',
      description: 'These cookies track your activity to deliver relevant advertisements and measure campaign effectiveness.',
      examples: [
        'Advertising network cookies for targeted ads',
        'Social media cookies for share functionality',
        'Retargeting cookies to show relevant content',
        'Conversion tracking pixels'
      ],
      duration: 'Up to 1 year'
    }
  ];

  const sections = [
    {
      title: '1. What Are Cookies?',
      content: [
        'Cookies are small text files that are placed on your device when you visit a website. They are widely used to make websites work more efficiently and provide a better user experience.',
        'Cookies contain information that is transferred to your device\'s hard drive. They help us recognize your device and remember your preferences for future visits.'
      ]
    },
    {
      title: '2. Why We Use Cookies',
      content: [
        'We use cookies for various purposes, including to ensure the proper functioning of our website, analyze how you use our services, personalize your experience, and deliver relevant advertising.',
        'Some cookies are essential for the operation of our website, while others help us improve our services, understand user behavior, and provide targeted content and advertisements.'
      ]
    },
    {
      title: '3. Third-Party Cookies',
      content: [
        'In addition to our own cookies, we may use various third-party cookies to report usage statistics, deliver advertisements, and provide enhanced functionality.',
        'Third-party services we use include Google Analytics, Facebook Pixel, LinkedIn Insight Tag, and various advertising networks. These third parties have their own privacy policies governing their use of cookies.'
      ]
    },
    {
      title: '4. Managing Your Cookie Preferences',
      content: [
        'You can control and manage cookies in various ways. Please note that removing or blocking cookies may impact your user experience and some features may no longer function properly.',
        'Most web browsers allow you to manage your cookie preferences through the browser settings. You can set your browser to refuse cookies or delete certain cookies. Generally, you should be able to manage similar technologies in the same way.',
        'You can also opt-out of interest-based advertising through the Digital Advertising Alliance (DAA) or the Network Advertising Initiative (NAI).'
      ]
    },
    {
      title: '5. Do Not Track Signals',
      content: [
        'Some web browsers incorporate a "Do Not Track" (DNT) feature that signals to websites you visit that you do not want to have your online activity tracked.',
        'Currently, there is no industry standard for how websites should respond to DNT signals. At this time, our website does not respond to DNT browser settings or signals.'
      ]
    },
    {
      title: '6. Cookie Duration',
      content: [
        'Session cookies are temporary and are deleted when you close your browser. Persistent cookies remain on your device until they expire or are manually deleted.',
        'The duration of persistent cookies varies depending on their purpose, ranging from a few hours to several years. You can view the expiration date of cookies stored on your device through your browser settings.'
      ]
    },
    {
      title: '7. Updates to This Policy',
      content: [
        'We may update this Cookie Policy from time to time to reflect changes in technology, legislation, our operations, or for other reasons.',
        'We will notify you of any material changes by posting the updated policy on our website with a new "Last Updated" date. We encourage you to review this policy periodically.'
      ]
    },
    {
      title: '8. Contact Us',
      content: [
        'If you have any questions about our use of cookies or this Cookie Policy, please contact us at:',
        'Email: privacy@time2find.com',
        'Phone: +1 (555) 123-4567',
        'Address: Time2Find Global Headquarters, New York, NY'
      ]
    }
  ];

  return (
    <div className="policy-page">
      {/* Hero Banner */}
      <section className="policy-hero-banner">
        <div className="policy-hero-content">
          <div className="policy-badge">
            <i className="fas fa-cookie-bite"></i>
            <span>Legal</span>
          </div>
          <h1 className="policy-hero-heading">Cookie Policy</h1>
          <p className="policy-hero-subtext">
            Learn how we use cookies and similar technologies to enhance your experience on our website.
          </p>
          <div className="policy-meta">
            <span className="policy-meta-item">
              <i className="fas fa-calendar-alt"></i>
              Last Updated: January 11, 2026
            </span>
            <span className="policy-meta-item">
              <i className="fas fa-clock"></i>
              8 min read
            </span>
          </div>
        </div>
      </section>

      <div className="policy-container">
        {/* Introduction */}
        <div className="policy-intro">
          <p>
            Time2Find uses cookies and similar tracking technologies to improve your browsing experience, 
            analyze website traffic, personalize content, and serve targeted advertisements.
          </p>
          <p>
            This Cookie Policy explains what cookies are, how we use them, and your choices regarding cookies. 
            By continuing to use our website, you consent to our use of cookies as described in this policy.
          </p>
        </div>

        {/* Cookie Types Grid */}
        <div className="cookie-types-section">
          <h2 className="cookie-types-heading">Types of Cookies We Use</h2>
          <div className="cookie-types-grid">
            {cookieTypes.map((type, index) => (
              <div key={index} className="cookie-type-card">
                <div className="cookie-type-icon">
                  <i className={`fas ${type.icon}`}></i>
                </div>
                <h3 className="cookie-type-title">{type.title}</h3>
                <p className="cookie-type-description">{type.description}</p>
                <div className="cookie-type-examples">
                  <p className="cookie-examples-label">Examples:</p>
                  <ul>
                    {type.examples.map((example, eIndex) => (
                      <li key={eIndex}>{example}</li>
                    ))}
                  </ul>
                </div>
                <div className="cookie-type-duration">
                  <i className="fas fa-clock"></i>
                  <span>Duration: {type.duration}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div className="policy-sections">
          {sections.map((section, index) => (
            <div key={index} className="policy-section">
              <h2 className="policy-section-title">{section.title}</h2>
              <div className="policy-section-content">
                {section.content.map((paragraph, pIndex) => (
                  <p key={pIndex}>{paragraph}</p>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Cookie Management Guide */}
        <div className="cookie-management-box">
          <div className="cookie-management-header">
            <div className="cookie-management-icon">
              <i className="fas fa-sliders-h"></i>
            </div>
            <h3 className="cookie-management-title">How to Manage Cookies</h3>
          </div>
          <div className="cookie-management-content">
            <p>You can manage your cookie preferences through your browser settings:</p>
            <div className="browser-links">
              <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="browser-link">
                <i className="fab fa-chrome"></i>
                Google Chrome
              </a>
              <a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer" className="browser-link">
                <i className="fab fa-firefox"></i>
                Mozilla Firefox
              </a>
              <a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="browser-link">
                <i className="fab fa-safari"></i>
                Safari
              </a>
              <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="browser-link">
                <i className="fab fa-edge"></i>
                Microsoft Edge
              </a>
            </div>
          </div>
        </div>

        {/* Summary Box */}
        <div className="policy-summary-box">
          <div className="policy-summary-icon">
            <i className="fas fa-info-circle"></i>
          </div>
          <div className="policy-summary-content">
            <h3 className="policy-summary-title">Quick Summary</h3>
            <p className="policy-summary-text">
              We use cookies to improve your experience, analyze site usage, and deliver personalized content. 
              Essential cookies are necessary for the site to function, while other cookies help us understand and improve your experience. 
              You can manage your cookie preferences through your browser settings at any time.
            </p>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="policy-cta">
          <h3 className="policy-cta-title">Questions About Cookies?</h3>
          <p className="policy-cta-text">
            If you have any questions or concerns about our cookie practices, we're here to help.
          </p>
          <a href="/contact" className="policy-cta-button">
            <i className="fas fa-envelope"></i>
            Contact Privacy Team
          </a>
        </div>
      </div>
    </div>
  );
}

export default CookiePolicy;
